import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  BarChart3, User, LayoutDashboard, Zap, Clock, PhoneCall, ArrowRight, Trophy, ListChecks, Hash, Sparkles, GitCompareArrows,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useDialer } from '@/contexts/DialerContext';
import { useAuthStore } from '@/stores/authStore';
import { useMembers } from '@/hooks/queries/useAdmin';
import { useTabParam } from '@/hooks/useTabParam';
import {
  useDialerHeatmap, useDialerNumbers, useDialerPerformance, useListCompletion, useListPhones, useQualificationAccuracy,
} from '@/hooks/queries/useDialerAnalytics';
import { fetchAllCalls } from '@/api/endpoints/analytics';
import { currentLocale, formatDurationWords, formatNumber, formatPercent } from '@/i18n/locale';
import {
  COMPARE_MODES, PERIODS, browserTimezone, bucketKey, bucketStarts, compareBounds, drillFilter, grainFor, periodDays, resolvePeriod,
  type CompareMode, type DrillMetric, type Period,
} from '@/lib/analyticsPeriod';
import AgentPerformanceTable from '@/components/analytics/AgentPerformanceTable';
import CallPerformanceMetrics from '@/components/analytics/CallPerformanceMetrics';
import InteractiveCharts from '@/components/analytics/InteractiveCharts';
import CallerStats from '@/components/analytics/CallerStats';
import ConnectRateHeatmap from '@/components/analytics/ConnectRateHeatmap';
import DayHourHeatmap from '@/components/analytics/DayHourHeatmap';
import CallerNumbersTable from '@/components/analytics/CallerNumbersTable';
import QualificationAccuracyTable from '@/components/analytics/QualificationAccuracyTable';
import ListCompletionTable from '@/components/analytics/ListCompletionTable';
import AIPerformanceCoach from '@/components/analytics/AIPerformanceCoach';
import ExportReports from '@/components/analytics/ExportReports';
import ListSelect from '@/components/analytics/ListSelect';
import { LoadError } from '@/components/analytics/LoadError';

/**
 * Performances (/ParallelDialer/analytics), W4 (audit B2-analytics).
 *
 * Every figure is computed by the server over the whole period
 * (POST /api/analytics/dialer/*, metric contracts of W2): the page used to
 * count the 500 newest calls the browser had loaded (T3/T-d3), so « 6
 * derniers mois » silently counted a few days. Connected = the call reached
 * the agent; conversion = meetings / connected (CEO-7).
 *
 * One toolbar: list, agent, period (labels that say what they count),
 * « Comparer à », conversation threshold, and on the overview one display
 * selector (chart, curve, table) instead of five mode buttons. Sub-tabs live
 * in `?tab=` (overview, lists, numbers, hours, dialer, accuracy), the list in
 * `?list=`.
 */

const TABS = ['overview', 'lists', 'numbers', 'hours', 'dialer', 'accuracy'] as const;
type Tab = (typeof TABS)[number];
type ViewMode = 'chart' | 'curve' | 'table';

const PERIOD_LABEL_KEYS: Record<Period, string> = {
  today: 'common:time.today',
  last_7_days: 'dialer:analytics.period.last7Days',
  last_14_days: 'dialer:analytics.period.last14Days',
  last_30_days: 'dialer:analytics.period.last30Days',
  last_6_months: 'dialer:analytics.period.last6Months',
  last_12_months: 'dialer:analytics.period.last12Months',
  custom: 'dialer:callHistory.period.custom',
};

const COMPARE_LABEL_KEYS: Record<CompareMode, string> = {
  none: 'dialer:analytics.compare.none',
  previous: 'dialer:analytics.compare.previous',
  prev_month: 'dialer:analytics.compare.prevMonth',
  prev_year: 'dialer:analytics.compare.prevYear',
  custom: 'dialer:callHistory.period.custom',
};

const stateOf = (q: { isLoading: boolean; isError: boolean; data?: unknown }): 'loading' | 'error' | 'ready' =>
  q.isError && !q.data ? 'error' : q.isLoading ? 'loading' : 'ready';

export default function AnalyticsScreen() {
  const { t } = useTranslation(['pages', 'common', 'dialer']);
  const navigate = useNavigate();
  const dialer = useDialer();
  const { sources, parallelCallsCount, conversationThreshold, setConversationThreshold, selectedCustomerId, maxCallAttempts } = dialer;

  const activeOrgId = useAuthStore((s) => s.activeMembership?.organisationId);
  const { data: members = [] } = useMembers(activeOrgId);
  const teamMembers = useMemo(
    () => members
      .filter((m) => m.is_active && m.role !== 'viewer')
      .map((m) => ({ email: m.email, full_name: m.full_name || m.username || m.email, name: m.full_name || m.username || m.email })),
    [members],
  );

  const tabs: Array<{ id: Tab; label: string; icon: typeof LayoutDashboard }> = [
    { id: 'overview', label: t('pages:analytics.tabs.overview'), icon: LayoutDashboard },
    { id: 'lists', label: t('dialer:analytics.tabs.listCompletion'), icon: ListChecks },
    { id: 'numbers', label: t('dialer:analytics.tabs.numbers'), icon: Hash },
    { id: 'hours', label: t('dialer:analytics.tabs.hours'), icon: Clock },
    { id: 'dialer', label: t('dialer:analytics.tabs.dialer'), icon: Zap },
    { id: 'accuracy', label: t('dialer:analytics.tabs.accuracy'), icon: Sparkles },
  ];
  const [tab, setTab] = useTabParam(TABS, 'overview');

  // ── Filters ───────────────────────────────────────────────────────────────
  const [searchParams, setSearchParams] = useSearchParams();
  const [period, setPeriod] = useState<Period>('last_7_days');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [agent, setAgent] = useState('all');
  const [compareMode, setCompareMode] = useState<CompareMode>('none');
  const [compareStart, setCompareStart] = useState('');
  const [compareEnd, setCompareEnd] = useState('');
  const [viewMode, setViewMode] = useState<ViewMode>('chart');
  const timezone = useMemo(() => browserTimezone(), []);
  const maxAttempts = Number(maxCallAttempts) > 0 ? Number(maxCallAttempts) : undefined;

  // Completion first: its list names are the complete set (the dialer's
  // `sources` come from the contacts the browser loaded, capped at 1000).
  const completionQ = useListCompletion({
    agent_email: agent !== 'all' ? agent : undefined,
    customer_id: selectedCustomerId ?? undefined,
    max_attempts: maxAttempts,
  });
  const listNames = useMemo(() => {
    const fromServer = (completionQ.data?.lists ?? []).map((l) => l.list).filter(Boolean);
    return fromServer.length > 0 ? fromServer : (sources || []);
  }, [completionQ.data, sources]);
  const requestedList = searchParams.get('list');
  const list = requestedList && listNames.includes(requestedList) ? requestedList : 'all';
  const setList = (src: string) => {
    const next = new URLSearchParams(searchParams);
    if (!src || src === 'all') next.delete('list'); else next.set('list', src);
    setSearchParams(next, { replace: true });
  };

  const bounds = useMemo(() => resolvePeriod(period, customStart, customEnd), [period, customStart, customEnd]);
  // Trends need a previous period: the one picked, or else the window of the
  // same length just before (never for custom dates: no trend without one).
  const effectiveCompare: CompareMode = compareMode !== 'none' ? compareMode : period === 'custom' ? 'none' : 'previous';
  const cmp = useMemo(() => compareBounds(effectiveCompare, bounds, compareStart, compareEnd), [effectiveCompare, bounds, compareStart, compareEnd]);
  const grain = grainFor(bounds);

  const filters = {
    from: bounds.start?.toISOString(),
    to: bounds.end?.toISOString(),
    agent_email: agent !== 'all' ? agent : undefined,
    list: list !== 'all' ? list : undefined,
    customer_id: selectedCustomerId ?? undefined,
    timezone,
  };
  const perfQ = useDialerPerformance({
    ...filters,
    conversation_threshold: conversationThreshold,
    grain,
    ...(cmp.start && cmp.end ? { compare_from: cmp.start.toISOString(), compare_to: cmp.end.toISOString() } : {}),
  });
  const heatmapQ = useDialerHeatmap(filters, tab === 'hours');
  const numbersQ = useDialerNumbers(filters, tab === 'numbers');
  const accuracyQ = useQualificationAccuracy({ from: filters.from, to: filters.to, customer_id: filters.customer_id }, tab === 'accuracy');
  const phonesQ = useListPhones(filters.list, filters.customer_id);

  const perf = perfQ.data;
  const totals = perf?.totals ?? null;
  const perfState = stateOf(perfQ);

  // ── Derived views ─────────────────────────────────────────────────────────
  const completion = useMemo(() => {
    const out: Record<string, { called: number; total: number; pct: number }> = {};
    let called = 0; let total = 0;
    for (const l of completionQ.data?.lists ?? []) {
      out[l.list] = { called: l.called, total: l.total, pct: l.total > 0 ? Math.round((l.called / l.total) * 100) : 0 };
      called += l.called; total += l.total;
    }
    out.__all = { called, total, pct: total > 0 ? Math.round((called / total) * 100) : 0 };
    return out;
  }, [completionQ.data]);
  // REV-W4-F10: nothing until the read answered (it showed « 0 / 0 » while
  // loading or after a failure); the tile says loading / « Indisponible ».
  const activeCompletion = completionQ.data ? completion[list === 'all' ? '__all' : list] : undefined;

  const sparkSeries = useMemo(() => {
    if (!perf) return null;
    const byKey = new Map(perf.series.map((s) => [s.key, s]));
    const starts = bucketStarts(bounds, grain, totals?.firstCallAt ? new Date(totals.firstCallAt) : null, totals?.lastCallAt ? new Date(totals.lastCallAt) : null);
    const pick = (k: 'total' | 'connected' | 'conversations' | 'meetings') => starts.map((s) => byKey.get(bucketKey(s, grain))?.[k] ?? 0);
    return { total: pick('total'), connected: pick('connected'), conversations: pick('conversations'), meetings: pick('meetings') };
  }, [perf, bounds, grain, totals]);

  const drillFilterFor = (metric: DrillMetric) => {
    if (filters.list && !phonesQ.data) return null;
    const f = drillFilter(metric, { bounds, agentEmail: filters.agent_email, phones: filters.list ? phonesQ.data : undefined, threshold: conversationThreshold });
    return filters.customer_id ? { $and: [f, { customer_id: filters.customer_id }] } : f;
  };

  const hourly = useMemo(() => {
    const byHour = new Map<number, { hour: number; total: number; connected: number; meetings: number }>();
    for (const c of heatmapQ.data?.cells ?? []) {
      const h = byHour.get(c.hour) ?? { hour: c.hour, total: 0, connected: 0, meetings: 0 };
      h.total += c.total; h.connected += c.connected; h.meetings += c.meetings;
      byHour.set(c.hour, h);
    }
    const stats = [...byHour.values()].sort((a, b) => a.hour - b.hour).map((h) => ({ ...h, connectRate: h.total > 0 ? (h.connected / h.total) * 100 : 0 }));
    const best = [...stats].filter((h) => h.total >= 3).sort((a, b) => b.connectRate - a.connectRate).slice(0, 5);
    const series = stats.length === 0 ? [] : Array.from({ length: stats[stats.length - 1]!.hour - stats[0]!.hour + 1 }, (_, i) => {
      const hour = stats[0]!.hour + i;
      return stats.find((s) => s.hour === hour) ?? { hour, total: 0, connected: 0, meetings: 0, connectRate: 0 };
    });
    const peak = stats.reduce<(typeof stats)[number] | null>((acc, h) => (!acc || h.total > acc.total ? h : acc), null);
    return { stats, best, series, peak, bestSet: new Set(best.map((h) => h.hour)) };
  }, [heatmapQ.data]);

  const dialerMetrics = useMemo(() => {
    if (!totals || totals.total === 0) return null;
    const first = totals.firstCallAt ? new Date(totals.firstCallAt).getTime() : 0;
    const last = totals.lastCallAt ? new Date(totals.lastCallAt).getTime() : 0;
    const spanSec = Math.max(0, (last - first) / 1000);
    const lines = parallelCallsCount || 3;
    const AVG_RING_SECONDS = 30;
    const sequential = totals.total * AVG_RING_SECONDS;
    return {
      callsPerHour: spanSec > 0 ? Math.round((totals.total / (spanSec / 3600)) * 10) / 10 : totals.total,
      avgDurationSec: totals.durationCount > 0 ? totals.durationSeconds / totals.durationCount : 0,
      avgTalkSec: totals.connected > 0 ? totals.talkSeconds / totals.connected : 0,
      configuredLines: lines,
      timeSavedSeconds: sequential - sequential / lines,
      sequentialSeconds: sequential,
      parallelSeconds: sequential / lines,
      // REV-W4-F11: no span (a single call, or all at the same second) has no
      // rate: null shows « — », not a 0 % that reads as « idle » (DES-19).
      efficiency: spanSec > 0 ? Math.min((totals.talkSeconds / spanSec) * 100, 100) : null,
      totalCalls: totals.total,
    };
  }, [totals, parallelCallsCount]);

  const loadExportCalls = async () => {
    const phones = filters.list ? (phonesQ.data ?? []) : undefined;
    const f = drillFilter('total', { bounds, agentEmail: filters.agent_email, phones, threshold: conversationThreshold });
    return fetchAllCalls(filters.customer_id ? { $and: [f, { customer_id: filters.customer_id }] } : f) as Promise<any[]>;
  };

  const hourLabel = (h: number) => t('dialer:analytics.hourLabel', { h });
  const noValue = t('common:states.noValue');
  const fmtDecimal = (n: number) => formatNumber(n, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  const fmtDate = (d: Date | null) => (d ? d.toLocaleDateString(currentLocale(), { day: '2-digit', month: '2-digit', year: 'numeric' }) : null);
  const empty = perfState === 'ready' && totals?.total === 0;

  const EmptyPeriod = () => (
    <div className="bg-card rounded-xl border border-border p-10 text-center space-y-3">
      <p className="text-sm text-foreground">{t('dialer:analytics.noCallsPeriod')}</p>
      <Button type="button" variant="outline" onClick={() => navigate('/ParallelDialer')}>{t('dialer:analytics.emptyCta')}</Button>
    </div>
  );

  return (
    <div className="space-y-6 min-w-0">
      {/* Title and subtitle: DialerLayout (one heading for every page). */}
      <div className="flex items-center gap-1 border-b border-border overflow-x-auto [scrollbar-width:thin]" role="tablist" aria-label={t('pages:analytics.title')}>
        {tabs.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            type="button"
            role="tab"
            id={`analytics-tab-${id}`}
            aria-selected={tab === id}
            aria-controls={`analytics-panel-${id}`}
            onClick={() => setTab(id)}
            className={`inline-flex items-center gap-1.5 px-4 min-h-[44px] text-sm font-medium border-b-2 -mb-px whitespace-nowrap shrink-0 ${
              tab === id ? 'text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
            }`}
            style={tab === id ? { borderColor: 'var(--vf-accent)' } : undefined}
          >
            <Icon className="w-3.5 h-3.5" aria-hidden style={tab === id ? { color: 'var(--vf-accent)' } : undefined} />
            {label}
          </button>
        ))}
      </div>

      {/* ── One toolbar for every tab ───────────────────────────────────── */}
      <div className="flex flex-col gap-3 min-w-0">
        <div className="flex flex-wrap items-center gap-2 min-w-0">
          <ListSelect sources={listNames} completion={completion} value={list} onChange={setList} />

          {teamMembers.length > 0 && (
            <Select value={agent} onValueChange={setAgent}>
              <SelectTrigger className="w-full sm:w-48 max-w-full" aria-label={t('dialer:analytics.allAgents')}>
                <User className="w-4 h-4 mr-2 shrink-0 text-muted-foreground" aria-hidden />
                <span className="flex-1 min-w-0 text-left"><SelectValue placeholder={t('dialer:analytics.allAgents')} /></span>
              </SelectTrigger>
              <SelectContent className="max-w-[min(22rem,calc(100vw-2rem))]">
                <SelectItem value="all">{t('dialer:analytics.allAgents')}</SelectItem>
                {teamMembers.map((m) => (
                  <SelectItem key={m.email} value={m.email}><span className="block truncate">{m.full_name}</span></SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {(
            <Select value={period} onValueChange={(v) => setPeriod(v as Period)}>
              <SelectTrigger className="w-full sm:w-52 max-w-full" aria-label={t('pages:callHistory.filters.dateRange')}>
                <SelectValue placeholder={t('pages:callHistory.filters.dateRange')} />
              </SelectTrigger>
              <SelectContent>
                {PERIODS.map((p) => <SelectItem key={p} value={p}>{t(PERIOD_LABEL_KEYS[p])}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          {period === 'custom' && (
            <div className="flex items-center gap-2 flex-wrap">
              <Input type="date" value={customStart} onChange={(e) => setCustomStart(e.target.value)} className="h-9 w-40" aria-label={t('dialer:analytics.filters.startDate')} />
              {/* i18n-exempt: an HTML entity, not text */}
              <span className="text-muted-foreground/60" aria-hidden>&rarr;</span>
              <Input type="date" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} className="h-9 w-40" aria-label={t('dialer:analytics.filters.endDate')} />
            </div>
          )}

          {tab === 'overview' && (
            <>
              <Select value={compareMode} onValueChange={(v) => setCompareMode(v as CompareMode)}>
                <SelectTrigger className="w-full sm:w-56 max-w-full" aria-label={t('dialer:analytics.compare.with')}>
                  <GitCompareArrows className="w-4 h-4 mr-2 shrink-0 text-muted-foreground" aria-hidden />
                  <span className="flex-1 min-w-0 text-left"><SelectValue /></span>
                </SelectTrigger>
                <SelectContent>
                  {COMPARE_MODES.map((m) => <SelectItem key={m} value={m}>{t(COMPARE_LABEL_KEYS[m])}</SelectItem>)}
                </SelectContent>
              </Select>
              {compareMode === 'custom' && (
                <div className="flex items-center gap-2 flex-wrap">
                  <Input type="date" value={compareStart} onChange={(e) => setCompareStart(e.target.value)} className="h-9 w-40" aria-label={t('dialer:analytics.compare.startDate')} />
                  {/* i18n-exempt: an HTML entity, not text */}
                  <span className="text-muted-foreground/60" aria-hidden>&rarr;</span>
                  <Input type="date" value={compareEnd} onChange={(e) => setCompareEnd(e.target.value)} className="h-9 w-40" aria-label={t('dialer:analytics.compare.endDate')} />
                </div>
              )}
            </>
          )}

          {(tab === 'overview' || tab === 'dialer') && (
            <Select value={String(conversationThreshold)} onValueChange={(v) => setConversationThreshold(Number(v))}>
              <SelectTrigger className="w-auto min-w-[9rem]" aria-label={t('dialer:analytics.filters.convThresholdTitle')}>
                <span className="flex-1 min-w-0 text-left mr-2"><span className="text-muted-foreground mr-1.5">{t('dialer:analytics.convLabel')}</span><SelectValue /></span>
              </SelectTrigger>
              <SelectContent>
                {[15, 30, 45, 60, 90, 120].map((sec) => <SelectItem key={sec} value={String(sec)}>{formatDurationWords(sec)}</SelectItem>)}
              </SelectContent>
            </Select>
          )}

          {tab === 'overview' && (
            <Select value={viewMode} onValueChange={(v) => setViewMode(v as ViewMode)}>
              <SelectTrigger className="w-full sm:w-44 max-w-full sm:ml-auto" aria-label={t('dialer:analytics.filters.viewModeLabel')}>
                <BarChart3 className="w-4 h-4 mr-2 shrink-0 text-muted-foreground" aria-hidden />
                <span className="flex-1 min-w-0 text-left"><SelectValue /></span>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="chart">{t('dialer:analytics.viewMode.chart')}</SelectItem>
                <SelectItem value="curve">{t('dialer:analytics.viewMode.curve')}</SelectItem>
                <SelectItem value="table">{t('dialer:analytics.viewMode.table')}</SelectItem>
              </SelectContent>
            </Select>
          )}
        </div>

        {tab === 'overview' && compareMode !== 'none' && (
          <p className="text-xs text-muted-foreground" data-testid="comparaison-bornes">
            {cmp.start && cmp.end ? `${fmtDate(cmp.start)} → ${fmtDate(cmp.end)}` : t('dialer:analytics.compare.boundsUnavailable')}
          </p>
        )}
      </div>

      {/* ── Overview ─────────────────────────────────────────────────────── */}
      {tab === 'overview' && (
        <div role="tabpanel" id="analytics-panel-overview" aria-labelledby="analytics-tab-overview" className="space-y-6">
          <CallPerformanceMetrics
            state={perfState}
            error={perfQ.error}
            onRetry={() => perfQ.refetch()}
            totals={totals}
            previous={perf?.previous ?? null}
            comparePeriodLabel={compareMode !== 'none' ? t(COMPARE_LABEL_KEYS[compareMode]) : null}
            series={sparkSeries}
            conversationThreshold={conversationThreshold}
            periodDays={periodDays(bounds)}
            contactsConsumed={activeCompletion?.called}
            contactsTotal={activeCompletion?.total}
            contactsState={stateOf(completionQ)}
            onRetryContacts={() => { void completionQ.refetch(); }}
            drillFilterFor={drillFilterFor}
          />
          {perf && !empty && (
            viewMode === 'table' ? (
              <>
                <div className="relative overflow-x-auto vf-scroll-visible"><AgentPerformanceTable rows={perf.byAgent} agents={teamMembers} totals={totals} /></div>
                <CallerStats totals={totals} />
              </>
            ) : (
              <>
                <div className="relative overflow-x-auto vf-scroll-visible"><AgentPerformanceTable rows={perf.byAgent} agents={teamMembers} totals={totals} /></div>
                <div className="grid lg:grid-cols-3 gap-6 min-w-0">
                  <div className="lg:col-span-2 min-w-0">
                    <InteractiveCharts series={perf.series} grain={grain} viewMode={viewMode} periodStart={bounds.start} periodEnd={bounds.end} firstCallAt={totals?.firstCallAt} lastCallAt={totals?.lastCallAt} />
                  </div>
                  <div className="space-y-6 min-w-0"><CallerStats totals={totals} /></div>
                </div>
              </>
            )
          )}
          {empty && <EmptyPeriod />}
          {perf && !empty && <AIPerformanceCoach totals={totals} />}
        </div>
      )}

      {/* ── List completion ──────────────────────────────────────────────── */}
      {tab === 'lists' && (
        <div role="tabpanel" id="analytics-panel-lists" aria-labelledby="analytics-tab-lists" className="space-y-6">
          <p className="text-xs text-muted-foreground">{t('dialer:analytics.lists.allTime')}</p>
          <ListCompletionTable
            rows={(completionQ.data?.lists ?? []).filter((l) => list === 'all' || l.list === list)}
            state={stateOf(completionQ)}
            error={completionQ.error}
            onRefresh={() => completionQ.refetch()}
          />
          <ExportReports
            callCount={totals?.total ?? 0}
            loadCalls={loadExportCalls}
            agents={teamMembers}
            sourceCompletion={completion}
            activeSource={list}
            analyticsPeriod={period}
          />
        </div>
      )}

      {/* ── Numbers ──────────────────────────────────────────────────────── */}
      {tab === 'numbers' && (
        <div role="tabpanel" id="analytics-panel-numbers" aria-labelledby="analytics-tab-numbers">
          <CallerNumbersTable rows={numbersQ.data?.numbers ?? []} state={stateOf(numbersQ)} error={numbersQ.error} onRetry={() => numbersQ.refetch()} />
        </div>
      )}

      {/* ── Dialer ───────────────────────────────────────────────────────── */}
      {tab === 'dialer' && (
        <div role="tabpanel" id="analytics-panel-dialer" aria-labelledby="analytics-tab-dialer" className="space-y-6">
          {perfState === 'error' ? (
            <LoadError error={perfQ.error} onRetry={() => perfQ.refetch()} className="bg-card rounded-xl border border-border p-5" />
          ) : perfState === 'loading' ? (
            <div className="h-32 animate-pulse motion-reduce:animate-none rounded-xl bg-[var(--vf-surface-3)]" />
          ) : !dialerMetrics ? (
            <EmptyPeriod />
          ) : (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <DialerFigure icon={Zap} label={t('dialer:analytics.dialerTab.callsPerHour')} value={dialerMetrics.callsPerHour > 0 ? fmtDecimal(dialerMetrics.callsPerHour) : noValue} sub={t('dialer:analytics.dialerTab.outOfCalls', { count: dialerMetrics.totalCalls, value: formatNumber(dialerMetrics.totalCalls) })} />
                <DialerFigure icon={PhoneCall} label={t('dialer:analytics.dialerTab.parallelLines')} value={String(dialerMetrics.configuredLines)} sub={t('dialer:analytics.dialerTab.configured')} />
                <DialerFigure icon={Clock} label={t('dialer:analytics.dialerTab.timeSaved')} value={formatDurationWords(dialerMetrics.timeSavedSeconds)} sub={t('dialer:analytics.dialerTab.vsSequential', { count: dialerMetrics.configuredLines })} />
                <DialerFigure icon={Clock} label={t('dialer:analytics.dialerTab.avgCallDuration')} value={dialerMetrics.avgDurationSec > 0 ? formatDurationWords(dialerMetrics.avgDurationSec) : noValue} sub={t('dialer:analytics.dialerTab.talk', { duration: dialerMetrics.avgTalkSec > 0 ? formatDurationWords(dialerMetrics.avgTalkSec) : noValue })} />
              </div>

              <section className="bg-card rounded-xl border border-border overflow-hidden">
                <div className="px-5 py-4 border-b border-border/50 flex items-center gap-2">
                  <Zap className="w-5 h-5" aria-hidden style={{ color: 'var(--vf-accent)' }} />
                  <h2 className="text-sm font-semibold text-foreground">{t('dialer:analytics.dialerTab.breakdownTitle')}</h2>
                </div>
                <div className="p-6">
                  <div className="grid grid-cols-3 gap-6 text-center mb-6">
                    <div className="min-w-0">
                      <p className="text-xs text-muted-foreground mb-1 truncate">{t('dialer:analytics.dialerTab.sequentialEstimated')}</p>
                      <p className="text-2xl font-bold text-foreground tabular-nums truncate">{formatDurationWords(dialerMetrics.sequentialSeconds)}</p>
                      <p className="text-[10px] text-muted-foreground mt-1 truncate">{t('dialer:analytics.dialerTab.ringPerCall', { count: dialerMetrics.totalCalls, value: formatNumber(dialerMetrics.totalCalls) })}</p>
                    </div>
                    <div className="flex flex-col items-center justify-center min-w-0">
                      <ArrowRight className="w-6 h-6 text-muted-foreground shrink-0" aria-hidden />
                      <Badge className="mt-1 text-xs">
                        {formatPercent(-((dialerMetrics.configuredLines - 1) / dialerMetrics.configuredLines) * 100, 0)}
                      </Badge>
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs text-muted-foreground mb-1 truncate">{t('dialer:analytics.dialerTab.parallelEstimated')}</p>
                      <p className="text-2xl font-bold text-foreground tabular-nums truncate">{formatDurationWords(dialerMetrics.parallelSeconds)}</p>
                      <p className="text-[10px] text-muted-foreground mt-1 truncate">{t('dialer:analytics.dialerTab.simultaneousLines', { count: dialerMetrics.configuredLines })}</p>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">{t('dialer:analytics.dialerTab.utilizationRate')}</span>
                      <span className="font-semibold text-foreground tabular-nums" data-testid="dialer-efficiency">{dialerMetrics.efficiency === null ? '—' : formatPercent(dialerMetrics.efficiency)}</span>
                    </div>
                    <div className="h-2.5 bg-muted rounded-full overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${dialerMetrics.efficiency === null ? 0 : Math.max(dialerMetrics.efficiency, 1)}%`, background: 'var(--vf-accent)' }} />
                    </div>
                    <p className="text-xs text-muted-foreground">{t('dialer:analytics.dialerTab.utilizationRatio')}</p>
                  </div>
                </div>
              </section>

              <div className="bg-muted/40 border border-border/50 rounded-xl p-4 text-xs text-muted-foreground">
                <strong>{t('dialer:analytics.dialerTab.methodologyTitle')}</strong> {t('dialer:analytics.dialerTab.methodologyBody')}
              </div>
            </>
          )}
        </div>
      )}

      {/* ── Hours: heatmap day × hour, then by hour, then by zone ───────── */}
      {tab === 'hours' && (
        <div role="tabpanel" id="analytics-panel-hours" aria-labelledby="analytics-tab-hours" className="space-y-6">
          <DayHourHeatmap cells={heatmapQ.data?.cells ?? []} state={stateOf(heatmapQ)} error={heatmapQ.error} onRetry={() => heatmapQ.refetch()} />

          {hourly.series.length > 0 && hourly.peak && (
            <section className="bg-card rounded-xl border border-border overflow-hidden min-w-0" data-testid="analytics-hourly-chart">
              <div className="px-5 py-4 border-b border-border/50 flex items-start gap-2 min-w-0">
                <BarChart3 className="w-5 h-5 shrink-0 mt-0.5" aria-hidden style={{ color: 'var(--vf-text-3)' }} />
                <div className="min-w-0">
                  <h2 className="text-sm font-semibold text-foreground">
                    {hourly.best.length === 0
                      ? t('dialer:analytics.hoursTab.findingPeak', { peak: hourLabel(hourly.peak.hour), value: formatNumber(hourly.peak.total) })
                      : hourly.best[0]!.hour === hourly.peak.hour
                        ? t('dialer:analytics.hoursTab.findingSame', { hour: hourLabel(hourly.peak.hour), rate: formatPercent(hourly.best[0]!.connectRate) })
                        : t('dialer:analytics.hoursTab.finding', { peak: hourLabel(hourly.peak.hour), best: hourLabel(hourly.best[0]!.hour), rate: formatPercent(hourly.best[0]!.connectRate) })}
                  </h2>
                  <p className="text-xs mt-0.5" style={{ color: 'var(--vf-text-3)' }}>{t('dialer:analytics.hoursTab.volumeTitle')}</p>
                </div>
              </div>
              <div className="p-5">
                <div className="flex items-end gap-1 sm:gap-1.5">
                  {(() => {
                    const maxVol = Math.max(...hourly.series.map((h) => h.total), 1);
                    const dense = hourly.series.length > 12;
                    return hourly.series.map((h, i) => {
                      const top = hourly.bestSet.has(h.hour);
                      const tip = t('dialer:analytics.hoursTab.barTooltip', { label: hourLabel(h.hour), value: formatNumber(h.total), rate: formatPercent(h.connectRate) });
                      return (
                        <div key={h.hour} className="flex flex-col items-center gap-1 flex-1 min-w-0" title={tip}>
                          <span className="text-[10px] tabular-nums leading-none" style={{ color: top ? 'var(--vf-accent)' : 'var(--vf-text-3)' }}>{h.total > 0 ? formatNumber(h.total) : ''}</span>
                          <div className="w-full flex flex-col justify-end" style={{ height: 112 }}>
                            <div className="w-full rounded-t" style={{ height: h.total > 0 ? `${Math.max((h.total / maxVol) * 112, 3)}px` : 0, background: top ? 'var(--vf-accent)' : 'var(--vf-text-4)', opacity: top ? 1 : 0.55 }} />
                          </div>
                          <span className={`text-[10px] tabular-nums whitespace-nowrap leading-none ${dense && i % 2 === 1 ? 'invisible sm:visible' : ''}`} style={{ color: 'var(--vf-text-3)' }}>{hourLabel(h.hour)}</span>
                        </div>
                      );
                    });
                  })()}
                </div>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 pt-3 mt-3 border-t border-border/50 text-[11px]" style={{ color: 'var(--vf-text-3)' }}>
                  <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: 'var(--vf-accent)' }} />{t('dialer:analytics.hoursTab.legendTop')}</span>
                  <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: 'var(--vf-text-4)', opacity: 0.55 }} />{t('dialer:analytics.hoursTab.legendOther')}</span>
                </div>
              </div>
            </section>
          )}

          {hourly.best.length > 0 && (
            <section className="bg-card rounded-xl border border-border overflow-hidden min-w-0">
              <div className="px-5 py-4 border-b border-border/50 flex items-center gap-2 flex-wrap">
                <Trophy className="w-5 h-5 shrink-0" aria-hidden style={{ color: 'var(--vf-text-3)' }} />
                <h2 className="text-sm font-semibold text-foreground">{t('dialer:analytics.hoursTab.bestSlots')}</h2>
                <Badge variant="secondary" className="text-xs ml-auto">{t('dialer:analytics.hoursTab.min3Calls')}</Badge>
              </div>
              <ol className="divide-y divide-border/50">
                {hourly.best.map((h, idx) => (
                  <li key={h.hour} className="px-5 py-3 grid grid-cols-[1.5rem_3rem_minmax(0,1fr)_6.5rem] sm:grid-cols-[1.5rem_3rem_minmax(0,1fr)_6.5rem_5rem_4rem] items-center gap-x-3 min-w-0">
                    <span className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold" style={idx === 0 ? { background: 'var(--vf-accent-bg)', color: 'var(--vf-accent)' } : { background: 'var(--vf-surface-3)', color: 'var(--vf-text-2)' }}>{idx + 1}</span>
                    <span className="text-sm font-semibold text-foreground tabular-nums whitespace-nowrap">{hourLabel(h.hour)}</span>
                    <div className="h-2 rounded-full overflow-hidden min-w-0" style={{ background: 'var(--vf-surface-3)' }}>
                      <div className="h-full rounded-full" style={{ width: `${Math.max((h.connectRate / Math.max(hourly.best[0]!.connectRate, 1)) * 100, 2)}%`, background: idx === 0 ? 'var(--vf-accent)' : 'var(--vf-text-4)' }} />
                    </div>
                    <span className="text-xs font-semibold tabular-nums text-right whitespace-nowrap">{t('dialer:analytics.hoursTab.contactRate', { rate: formatPercent(h.connectRate) })}</span>
                    <span className="hidden sm:block text-xs text-muted-foreground tabular-nums text-right whitespace-nowrap">{formatNumber(h.total)} {t('common:entity.call', { count: h.total })}</span>
                    <span className="hidden sm:block text-xs text-muted-foreground tabular-nums text-right whitespace-nowrap">{h.meetings > 0 ? `${formatNumber(h.meetings)} ${t('common:entity.meeting', { count: h.meetings })}` : ''}</span>
                  </li>
                ))}
              </ol>
            </section>
          )}

          {perf && perf.byPhonePrefix.length > 0 && <ConnectRateHeatmap prefixes={perf.byPhonePrefix} />}
        </div>
      )}

      {/* ── AI accuracy (CEO-12) ─────────────────────────────────────────── */}
      {tab === 'accuracy' && (
        <div role="tabpanel" id="analytics-panel-accuracy" aria-labelledby="analytics-tab-accuracy">
          <QualificationAccuracyTable data={accuracyQ.data} state={stateOf(accuracyQ)} error={accuracyQ.error} onRetry={() => accuracyQ.refetch()} />
        </div>
      )}
    </div>
  );
}

function DialerFigure({ icon: Icon, label, value, sub }: { icon: typeof Zap; label: string; value: string; sub: string }) {
  return (
    <div className="bg-card rounded-xl border border-border p-5 flex flex-col gap-2 min-w-0">
      <div className="flex items-center gap-2 min-w-0">
        <Icon className="w-4 h-4 text-muted-foreground shrink-0" aria-hidden />
        <span className="text-sm text-muted-foreground truncate">{label}</span>
      </div>
      <span className="text-3xl font-bold text-foreground tabular-nums truncate">{value}</span>
      <span className="text-xs text-muted-foreground truncate">{sub}</span>
    </div>
  );
}
